import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import React, { useMemo } from "react";
import ReplyMessageCard from "../../../foundation/ChatCard/ReplyCard";
import OwnMessageCard from "../../../foundation/ChatCard/OwnMessageCard";
import SystemMessageCard from "../../../foundation/ChatCard/SystemMessageCard";
import { formatShortTime, formatDateHeader, getDateKey } from "./utils/formatTime";

type Message = {
  phone?: string;
  type?: string;
  message?: string;
  timestamp?: string;
  admin_ref?: string;
  direction?: string;
};

interface ChatContainerProps {
  messages: Message[];
}

const ChatContainer = ({ messages }: ChatContainerProps) => {
  const { grouped: sortedAndGroupedMessages, statusMap } = useMemo(() => {
    // Sort messages oldest first (ascending by timestamp)
    const sorted = [...messages].sort((a, b) => {
      const aTime = a.timestamp ? new Date(a.timestamp).getTime() : 0;
      const bTime = b.timestamp ? new Date(b.timestamp).getTime() : 0;
      return aTime - bTime;
    });

    // Extract status activities (type starts with 'STATUS_') and build
    // a lookup of the most recent status per message id.
    const statusActivities = sorted.filter((m) => m.type && m.type.startsWith("STATUS_"));

    type StatusDetails = {
      delivery_status?: string;
      error_message?: string;
      raw?: any;
      timestamp?: string;
    };

    const statusMap: Record<string, StatusDetails> = {};

    const parseStatus = (s: Message) => {
      let payload: any = null;
      try {
        if (s.message) payload = JSON.parse(s.message);
      } catch (e) {
        payload = null;
      }

      const msgId = (payload && (payload.msg_id || payload.msgId)) || (s as any).msg_id || s.admin_ref || (s as any).admin_ref;
      const delivery_status = (payload && (payload.delivery_status || payload.deliveryStatus)) || (payload && payload.status) || (s as any).delivery_status || (s as any).deliveryStatus;
      const error_message = (payload && (payload.error_message || payload.errorMessage)) || (s as any).error_message || (s as any).errorMessage;

      return { msgId, delivery_status, error_message, raw: payload || s } as StatusDetails & { msgId?: string };
    };

    statusActivities.forEach((s) => {
      const parsed = parseStatus(s) as any;
      const key = parsed.msgId;
      if (!key) return;

      const existing = statusMap[key];
      const existingTime = existing && existing.timestamp ? new Date(existing.timestamp).getTime() : 0;
      const thisTime = s.timestamp ? new Date(s.timestamp).getTime() : Date.now();

      if (!existing || thisTime >= existingTime) {
        statusMap[key] = {
          delivery_status: parsed.delivery_status,
          error_message: parsed.error_message,
          raw: parsed.raw,
          timestamp: s.timestamp,
        };
      }
    });

    // Group by date and create a flat list with date headers
    const grouped: (Message & { isDateHeader?: boolean; headerText?: string })[] = [];
    let lastDateKey = '';

    // Filter out status activities from the timeline display itself
    const filtered = sorted.filter((msg) => !(msg.type && msg.type.startsWith("STATUS_")));

    filtered.forEach((msg) => {
      const dateKey = msg.timestamp ? getDateKey(msg.timestamp) : '';

      // Add date header if date changed
      if (dateKey && dateKey !== lastDateKey) {
        lastDateKey = dateKey;
        grouped.push({
          isDateHeader: true,
          headerText: formatDateHeader(msg.timestamp!),
          timestamp: msg.timestamp,
        } as any);
      }

      grouped.push(msg);
    });

    return { grouped, statusMap };
  }, [messages]);

  return (
    <Box
      sx={{
        flex: 1,
        width: "100%",
        position: "relative",
        display: "flex",
        flexDirection: "column",
        paddingTop: "1%",
        paddingBottom: "1%",
        gap: ".5rem",
        overflowY: "auto",
        overflowX: "hidden",
      }}
    >
      {sortedAndGroupedMessages.map((item, idx) => {
        // Render date header
        if ((item as any).isDateHeader) {
          return (
            <Box
              key={`header-${idx}`}
              display="flex"
              justifyContent="center"
              my={2}
            >
              <Typography
                variant="caption"
                sx={{
                  color: "#8696a1",
                  fontSize: "0.75rem",
                  textTransform: "uppercase",
                }}
              >
                {(item as any).headerText}
              </Typography>
            </Box>
          );
        }

        // Render message card based on direction
        const m = item as Message;
        const shortTime = m.timestamp ? formatShortTime(m.timestamp) : "";
        const isOutgoing = m.direction === "OUTGOING";
        const isSystem = m.direction === "SYSTEM";

        console.log('ChatContainer rendering message:', { message: m.message, type: m.type, direction: m.direction, timestamp: m.timestamp });

        // Determine status info for outgoing messages. We try several
        // fallbacks to obtain a matching message id (admin_ref or JSON payload).
        // Also check for delivery_status directly on the activity (from chat-history API).
        let statusInfo: any = null;
        if (isOutgoing) {
          // First, check if activity has delivery_status directly (chat-history API)
          const activityDeliveryStatus = (m as any).delivery_status;
          const activityErrorMessage = (m as any).error_message;

          if (activityDeliveryStatus) {
            statusInfo = {
              delivery_status: activityDeliveryStatus,
              error_message: activityErrorMessage,
              raw: m,
              timestamp: m.timestamp,
            };
          }

          // Also try to find status from statusMap (feed API with STATUS_ activities)
          let msgIdKey: string | undefined;
          try {
            const parsed = m.message ? JSON.parse(m.message) : null;
            msgIdKey = (parsed && (parsed.msg_id || parsed.msgId)) || m.admin_ref || (m as any).msg_id;
          } catch (e) {
            msgIdKey = m.admin_ref || (m as any).msg_id;
          }

          if (msgIdKey && (statusMap as any)[msgIdKey]) {
            // Prefer statusMap (more recent status) if available
            statusInfo = (statusMap as any)[msgIdKey];
          }
        }

        // Full-width container for message alignment
        return (
          <Box
            key={`msg-${idx}`}
            display="flex"
            justifyContent={isSystem ? "center" : (isOutgoing ? "flex-end" : "flex-start")}
            paddingX="6%"
          >
            {isSystem ? (
              <SystemMessageCard
                text={m.message}
              />
            ) : isOutgoing ? (
              <OwnMessageCard
                text={m.message}
                timestamp={shortTime}
                status={statusInfo}
              />
            ) : (
              <ReplyMessageCard
                details={m.message}
                timestamp={shortTime}
              />
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default ChatContainer;
